import { useRef, type CSSProperties, type ReactNode } from 'react';
import { color } from '../styles/theme';
import { ApiError } from '../api/client';
import { useRemembered } from '../lib/remembered';
import { Button, Card, CardTitle } from '../components/ui';
import { IconUpload } from '../components/icons';

/**
 * Pieces the REDCap tabs share: how a refusal reads, a pill, a note, a file
 * picker — and the two cards that make each tab explain itself: the guide
 * ("How to do this", numbered, with where each step happens) and the pointer
 * (what to do on an earlier step before this one can start).
 */

export const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export interface Problem {
  message: string;
  columns?: Array<{ column: string; why: string }>;
  problems?: string[];
}

/** What the API said, with the refused columns or map problems when it named them. */
export function problemOf(err: unknown): Problem {
  if (err instanceof ApiError) {
    const details = err.details as { columns?: unknown; problems?: unknown } | undefined;
    const columns = Array.isArray(details?.columns)
      ? (details!.columns as Array<{ column?: unknown; why?: unknown } | null>).filter(
          (c): c is { column: string; why: string } =>
            typeof c?.column === 'string' && typeof c?.why === 'string',
        )
      : undefined;
    const problems = Array.isArray(details?.problems)
      ? (details!.problems as unknown[]).filter((p): p is string => typeof p === 'string')
      : undefined;
    return {
      message: err.message,
      columns: columns?.length ? columns : undefined,
      problems: problems && problems.length > 1 ? problems : undefined,
    };
  }
  return { message: (err as Error)?.message || 'Something went wrong.' };
}

export function ProblemNote({ problem }: { problem: Problem }) {
  return (
    <div
      role="alert"
      style={{
        background: '#fdf1ee',
        border: '1px solid #f3d5cc',
        color: color.red,
        borderRadius: 14,
        padding: '12px 16px',
        fontSize: 13.5,
        fontWeight: 600,
      }}
    >
      {problem.message}
      {problem.columns && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontWeight: 600 }}>
          {problem.columns.map((c) => (
            <li key={c.column}>
              <code style={{ fontFamily: MONO }}>{c.column}</code>
              {' — '}
              {c.why}
            </li>
          ))}
        </ul>
      )}
      {problem.problems && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontWeight: 600 }}>
          {problem.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Pill({
  tone,
  children,
  title,
}: {
  tone: { bg: string; fg: string };
  children: ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 800,
        padding: '4px 9px',
        borderRadius: 999,
        background: tone.bg,
        color: tone.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return <p style={{ fontSize: 12.5, color: color.muted, margin: 0, lineHeight: '18px' }}>{children}</p>;
}

export function downloadText(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** A button that opens the file picker; the input is reset so the same file can be picked twice. */
export function FileButton({
  label,
  disabled,
  onFile,
  variant,
}: {
  label: string;
  disabled?: boolean;
  onFile: (file: File) => void;
  variant?: 'primary' | 'ghost' | 'soft';
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button variant={variant} onClick={() => input.current?.click()} disabled={disabled}>
        <IconUpload /> {label}
      </Button>
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </>
  );
}

/** A small uppercase label over a value, as the record panels use. */
export function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ background: color.tint, borderRadius: 14, padding: '10px 12px', minWidth: 0 }}>
      <p
        style={{
          fontSize: 10.5,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: color.muted,
          margin: '0 0 3px',
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: 15, fontWeight: 700, color: color.ink, margin: 0 }}>{value}</p>
    </div>
  );
}

/* ── Teaching cards ─────────────────────────────────────────────────────── */

const linkButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 12.5,
  fontWeight: 700,
  color: color.blue,
  font: 'inherit',
};

export interface GuideStep {
  /** The step, as an instruction: "Upload the data dictionary." */
  do: ReactNode;
  /** Where it happens and how to find it: "In REDCap: Project Setup → Data Dictionary." */
  where?: ReactNode;
}

/**
 * "How to do this" — one tab's steps in order, each saying where it happens
 * (in REDCap, or here) so a newcomer can follow it without asking. Open the
 * first time; stays closed once closed, like the About panels elsewhere: a
 * screen has to teach once, then stay out of the way.
 */
export function GuideCard({
  storageKey,
  title = 'How to do this',
  intro,
  steps,
}: {
  storageKey: string;
  title?: string;
  intro?: ReactNode;
  steps: ReadonlyArray<GuideStep>;
}) {
  const [open, setOpen] = useRemembered(storageKey, true);
  return (
    <Card pad={open ? '14px 20px 16px' : '10px 20px'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 13, color: color.navy, flex: 1 }}>{title}</strong>
        <button onClick={() => setOpen(!open)} aria-expanded={open} style={{ ...linkButton, fontSize: 12.5, fontWeight: 700 }}>
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {open && (
        <>
          {intro && <p style={{ fontSize: 13, color: color.ink, lineHeight: '20px', margin: '6px 0 0' }}>{intro}</p>}
          <ol
            style={{
              margin: '12px 0 0',
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
              gap: '12px 20px',
            }}
          >
            {steps.map((step, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
                <span
                  aria-hidden="true"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: color.tint2,
                    color: color.blueDeep,
                    fontSize: 11.5,
                    fontWeight: 800,
                    flex: 'none',
                    marginTop: 1,
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ minWidth: 0, fontSize: 13, lineHeight: '19px' }}>
                  <span style={{ display: 'block', fontWeight: 700, color: color.ink }}>{step.do}</span>
                  {step.where && (
                    <span style={{ display: 'block', color: color.muted, fontSize: 12.5, lineHeight: '18px', marginTop: 2 }}>
                      {step.where}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </>
      )}
    </Card>
  );
}

/**
 * What an earlier step has to settle before this tab can start — with the
 * button that goes there. One sentence and one button, so the dead end
 * reads as "do that first", not as a broken screen.
 */
export function PointerCard({
  title,
  children,
  actionLabel,
  onAction,
}: {
  title: string;
  children: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <CardTitle style={{ marginBottom: 6 }}>{title}</CardTitle>
          <Note>{children}</Note>
        </div>
        {actionLabel && onAction && (
          <Button variant="soft" onClick={onAction}>
            {actionLabel}
          </Button>
        )}
      </div>
    </Card>
  );
}

/** "In REDCap:" or "Here:" in front of a guide step's where. */
export function Where({ at, children }: { at: 'REDCap' | 'here'; children: ReactNode }) {
  return (
    <>
      <strong style={{ color: color.ink, fontWeight: 700 }}>{at === 'REDCap' ? 'In REDCap:' : 'Here:'}</strong> {children}
    </>
  );
}
